

import { zodResolver } from '@hookform/resolvers/zod';
import { ArbreCreateInput, InventaireCreateInput } from '@repo/types';
import { EspeceSelect } from '@repo/ui/features/especes';
import { Alert } from '@repo/ui/components/ui/alert';
import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/components/ui/form';
import { Input } from '@repo/ui/components/ui/input';
import { Separator } from '@repo/ui/components/ui/separator';
import { services } from '@repo/ui/lib/api/services/index';
import { useMutation } from '@tanstack/react-query';
import { useInventaireErrorHandler } from '@repo/ui/hooks/api/inventaires/use-inventaire-error-handler';
import { AlertCircle, ChevronRight, Hash, Home, MapPin, Ruler, Tag, TreePine } from 'lucide-react';
import * as React from "react";
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { MapCoordinates, MapProviderContext, UnifiedMap } from '../map-view/providers';

type FormValues = {
  diametre: number | '';
  hauteur: number | '';
  code_site: string;
  num_sujet: string;
  espece_id: string; // as string for select
  position: MapCoordinates | null;
};

// Create a proper validation schema for the form fields
const inventaireFormSchema = z.object({
  diametre: z.union([z.number().min(0, 'Le diamètre doit être positif'), z.literal('')]).optional(),
  hauteur: z.union([z.number().min(0, 'La hauteur doit être positive'), z.literal('')]).optional(),
  code_site: z.string().optional(),
  num_sujet: z.string().optional(),
  espece_id: z.string().min(1, 'Sélectionnez une espèce'),
  position: z.object({
    lat: z.number(),
    lng: z.number(),
  }).nullable().optional(),
});

interface UseInventaireFormProps {
  onSuccess: () => void;
}

export function useInventaireForm({ onSuccess }: UseInventaireFormProps) {
  const [position, setPosition] = useState<MapCoordinates | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    //@ts-ignore
    resolver: zodResolver(inventaireFormSchema),
    defaultValues: {
      diametre: '',
      hauteur: '',
      code_site: '',
      num_sujet: '',
      espece_id: '',
      position: null,
    },
  });

  const createInventaire = useMutation({
    mutationFn: async (values: FormValues) => {
      console.log("Form values:", values);

      if (!values.espece_id) {
        form.setError('espece_id', { message: 'Sélectionnez une espèce' });
        throw new Error('Espèce requise');
      }
      if (!values.position) {
        form.setError('position', { message: 'Sélectionnez une position sur la carte' });
        throw new Error('Position requise');
      }

      // 1) Create arbre first
      const arbrePayload: ArbreCreateInput = {
        diametre: values.diametre === '' ? null : Number(values.diametre),
        hauteur: values.hauteur === '' ? null : Number(values.hauteur),
        code_site: values.code_site || null,
        num_sujet: values.num_sujet || null,
        // Send as JSON string so backend geospatial service converts properly
        coord: values.position ? JSON.stringify({ lat: values.position.lat, lng: values.position.lng }) : null,
        espece_id: values.espece_id ? Number(values.espece_id) : undefined,
      } as any;


      const arbreRes = await services.arbre.createArbre(arbrePayload);

      // 2) Create inventaire linked to that arbre
      const inventairePayload: InventaireCreateInput = {
        user_id: undefined, // backend sets from current user
        arbre_id: arbreRes.id,
        epaysage_id: null,
        is_finished: false,
        type: "ARBRE",
        creation_source: 'WEB',
      } as any;

      console.log("Inventaire payload:", inventairePayload);
      const invRes = await services.inventory.createInventaire(inventairePayload);
      return invRes;
    },
  });

  const { handleError } = useInventaireErrorHandler();

  const onSubmit = async (values: FormValues) => {
    setError(null);
    try {
      console.log("Submitting form with values:", values);
      await createInventaire.mutateAsync(values);
      onSuccess();
    } catch (error) {
      console.error('Error creating inventaire:', error);
      // Gérer les erreurs de limite d'usage avec le modal
      handleError(error);
      // Afficher aussi l'erreur dans le formulaire pour les autres types d'erreurs
      if (!(error instanceof Error && (error as any)?.response?.data?.message?.includes("Limite"))) {
        setError('Une erreur est survenue lors de la création de l\'inventaire');
      }
    }
  };

  return {
    form,
    error,
    position,
    setPosition,
    isSubmitting: createInventaire.isPending,
    onSubmit,
  };
}

interface InventaireFormFieldsProps {
  form: ReturnType<typeof useForm<FormValues>>;
  error: string | null;
  position: MapCoordinates | null;
  setPosition: (position: MapCoordinates | null) => void;
  isSubmitting: boolean;
  onCancel: () => void;
  onSubmit: (data: FormValues) => Promise<void>;
}

export function InventaireFormFields({
  form,
  error,
  position,
  setPosition,
  isSubmitting,
  onCancel,
  onSubmit,
}: InventaireFormFieldsProps) {
  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Simple Header with Breadcrumbs */}
      <div className="space-y-4">
        {/* Breadcrumbs */}
        <nav className="flex items-center space-x-2 text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <Home className="h-4 w-4" />
            <span>Inventaires</span>
          </div>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium">Nouveau</span>
        </nav>

        {/* Simple Title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Nouveau inventaire
            </h1>
            <p className="text-muted-foreground mt-1">
              Créez un nouvel inventaire d'arbre
            </p>
          </div>
          <Badge variant="secondary" className="text-sm">
            Type: Arbre
          </Badge>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side - Form Inputs */}
            <div className="space-y-6">
              {/* All Form Fields in One Card */}
              <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50/50">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <TreePine className="h-5 w-5 text-primary" />
                    Informations de l'arbre
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Measurements */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      <Ruler className="h-4 w-4" />
                      Mesures
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="diametre"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium flex items-center gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                              Diamètre (cm)
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="ex: 30"
                                value={field.value as any}
                                onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                                className="h-12 border-2 focus:border-primary/50 transition-colors"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="hauteur"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium flex items-center gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                              Hauteur (m)
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                placeholder="ex: 12"
                                value={field.value as any}
                                onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                                className="h-12 border-2 focus:border-primary/50 transition-colors"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Identification */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      <Hash className="h-4 w-4" />
                      Identification
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="code_site"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium flex items-center gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                              Code site
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Code site"
                                {...field}
                                className="h-12 border-2 focus:border-primary/50 transition-colors"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="num_sujet"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium flex items-center gap-2">
                              <div className="w-2 h-2 bg-primary rounded-full"></div>
                              Num sujet
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Num sujet"
                                {...field}
                                className="h-12 border-2 focus:border-primary/50 transition-colors"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Species */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Espèce
                    </h3>
                    <FormField
                      control={form.control}
                      name="espece_id"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-sm font-medium flex items-center gap-2">
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                            Sélectionner une espèce
                          </FormLabel>
                          <EspeceSelect
                            value={field.value || undefined}
                            onChange={(value) => field.onChange(value ?? '')}
                            searchMode="server"
                            groupBy="categorie"
                            fetchFilters={{ limit: 1000, sortBy: 'recent' }}
                            placeholder="Sélectionner une essence"
                            emptyMessage="Aucune essence trouvée"
                            variant="form"
                            className="w-full"
                          />
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  className="h-12 px-8 border-2 hover:bg-slate-50"
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  onClick={form.handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                  className="h-12 px-8 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg"
                >
                  {isSubmitting ? 'Enregistrement...' : 'Créer l\'inventaire'}
                </Button>
              </div>
            </div>

            {/* Right Side - Map */}
            <div className="space-y-4">
              <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-slate-50/50 h-full">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <MapPin className="h-5 w-5 text-primary" />
                    Localisation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="position"
                    render={({ field }) => (
                      <FormItem className="h-full">
                        <FormLabel className="text-sm font-medium flex items-center gap-2">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          Position sur la carte
                        </FormLabel>
                        <FormControl>
                          <div className="h-[500px] w-full rounded-xl overflow-hidden border-2 border-slate-200 shadow-inner">
                            <MapProviderContext defaultProvider={"google"}>
                              <UnifiedMap
                                options={{
                                  center: position || { lat: 36.8065, lng: 10.1815 },
                                  zoom: 12,
                                  scrollWheelZoom: true,
                                }}
                                eventHandlers={{
                                  onMapClick: (coords) => {
                                    setPosition(coords);
                                    field.onChange(coords);
                                  },
                                }}
                                markers={position ? [{ id: 'pos', position, title: 'Position sélectionnée' }] : []}
                              />
                            </MapProviderContext>
                          </div>
                        </FormControl>
                        {position && (
                          <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
                            <MapPin className="h-4 w-4 text-primary" />
                            <span className="text-sm text-muted-foreground">
                              Lat: {position.lat.toFixed(6)}, Lng: {position.lng.toFixed(6)}
                            </span>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

interface CreateInventaireFormProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export function CreateInventaireForm({ onCancel, onSuccess }: CreateInventaireFormProps) {
  const { form, error, position, setPosition, isSubmitting, onSubmit } =
    useInventaireForm({ onSuccess });

  return (
    <InventaireFormFields
      form={form}
      error={error}
      position={position}
      setPosition={setPosition}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      onSubmit={onSubmit}
    />
  );
}


